import { SHBox, SHContainer, SHStack } from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { AnalysisSummaryDTO } from "@models/platform-analysis/entities/steps/summary";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { RootState } from "@redux/store";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useMount, useToggle } from "react-use";
import { SummaryStepSkeleton } from "../../components/skeletons/summary";
import { SummaryDisclaimers } from "./components/disclaimers";
import { SummaryRenderSectionContainer } from "./components/section-container";
import { SummaryStepName } from "./components/step-name";
import { repairUpdateAnalysis } from "@redux/slices/platform-analysis/util";
import { generateHiddenSubProducts } from "../fee/analysis/utils";
import { AnalysisFilterDTO } from "@models/platform-analysis/entities/analysis";
import { isEqual } from "lodash";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { savePlatformAnalysisFilters } from "@services/platform-analysis/platformAnalysisService";
import { MINIMUM_DELAY_TIME } from "@constants";

export const AnalysisSummaryStep: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }: StepCompProps, ref) => {
  const { analysisId } = useParams<{ analysisId: string }>();

  const {
    hiddenIds,
    disqualifiedHiddenIds,
    filterInvestmentMenuValue,
    filterMissingInvestmentValue,
    heatmapMode,
    displayStyle,
    displayMode,
    performanceScope,
  } = useSelector(
    (state: RootState) => state.platformAnalysis.internal.feeAnalysis,
  );

  const {
    platformAnalysis,
    isCompleted,
    isReadOnly,
    setIsDirty,
    loadPlatformAnalysis,
    loadPlatformAnalysisDisclaimers,
    updateCurrentPlatformAnalysis,
    setAnalysisDisclaimers,
  } = usePlatformAnalysis();

  const formMethods = useForm<AnalysisSummaryDTO>({
    mode: "onChange",
    defaultValues: new AnalysisSummaryDTO(),
  });

  const {
    reset,
    getValues,
    formState: { isDirty },
  } = formMethods;

  const storedIsLoading = useSelector(
    (state: RootState) => state.platformAnalysis?.ui?.isLoading,
  );
  const storedIsReview = useSelector(
    (state: RootState) => state.platformAnalysis?.platformAnalysis?.isReview,
  );

  // #region States
  const [isLoadingLocal, toggleLocalLoading] = useToggle(true);
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  // #endregion States

  // #region Handlers
  useImperativeHandle(ref, () => ({
    onChangeStep: async () => {
      if (!isReadOnly) {
        await saveFilter();
      }
      if (!isDirty || isReadOnly) return true;

      return await handleOnSubmit(true);
    },
    onChangeOtherStep: async () => {
      if (!isReadOnly) {
        await saveFilter();
      }
      if (isDirty) {
        return await handleOnSubmit(true);
      }

      return true;
    },
    onSubmit: async () => {
      return await handleOnSubmit(true);
    },
  }));

  const loadSummaryData = async (targetAnalysisId: string) => {
    const [analysis, disclaimers] = await Promise.all([
      loadPlatformAnalysis({
        platformAnalysisId: targetAnalysisId,
        stepId: AnalysisStep.Summary,
      }),
      loadPlatformAnalysisDisclaimers(targetAnalysisId),
    ]);

    if (!analysis || !analysis.summary) return;
    formMethods.reset(analysis.summary);
    setAnalysisDisclaimers(disclaimers);

    toggleLocalLoading(false);
  };

  const handleOnSubmit = async (isShowMessage?: boolean) => {
    if (!platformAnalysis) return false;

    toggleIsSubmitting(true);

    const analysis = await updateCurrentPlatformAnalysis(
      {
        ...repairUpdateAnalysis(platformAnalysis),
        currentStep: AnalysisStep.Summary,
        summary: {
          ...getValues(),
          selectedProducts: null,
          hiddenSubProducts: generateHiddenSubProducts(
            [...hiddenIds, ...disqualifiedHiddenIds],
            platformAnalysis?.summary?.feeAnalysis?.all.subProducts ?? [],
          ),
          analysisComments: null,
          selectedFeatures: null,
          featureAnalysis: null,
          businessMetricAnalysis: null,
          businessMetricSelectedFeatures: null,
          feeAnalysis: null,
        },
      },
      isShowMessage,
    );

    if (analysis) {
      reset({
        ...getValues(),
      });
    }

    setTimeout(() => {
      toggleIsSubmitting(false);
    }, MINIMUM_DELAY_TIME);

    return !!analysis;
  };

  const saveFilter = async () => {
    const data: AnalysisFilterDTO = {
      feeMissingInvestmentsStyle: filterMissingInvestmentValue,
      investmentMenu: filterInvestmentMenuValue,
      feesDisplayStyle: displayStyle,
      feeTableViewMode: heatmapMode,
      smaTableViewMode: displayMode,
      excludePerformanceInSMATable: isEqual(
        performanceScope,
        FeesPerformanceScope.Excluding,
      ),
      hiddenSubProducts: generateHiddenSubProducts(
        [...hiddenIds, ...disqualifiedHiddenIds],
        platformAnalysis?.summary?.feeAnalysis?.all.subProducts ?? [],
      ),
      showAnalysisScore: true,
      hidePortfolioCompositionSubtotals: true,
    };

    await savePlatformAnalysisFilters(platformAnalysis?.id ?? "", data);
  };
  // #endregion Handlers

  // #region Effects
  useMount(() => {
    if (!analysisId || analysisId === "new") return;
    loadSummaryData(analysisId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);
  // #endregion Effects

  if (isLoadingLocal || storedIsLoading) return <SummaryStepSkeleton />;

  return (
    <SHContainer maxWidth="lg2" sx={{ px: { xs: "16px", lg2: 0 }, py: "25px" }}>
      <SHStack rowGap={3}>
        <SummaryStepName stepNumber={stepNumber || 0} />
        <FormProvider {...formMethods}>
          <SHBox
            component={"form"}
            display={"flex"}
            flexDirection={"column"}
            gap={3}
          >
            <SummaryRenderSectionContainer
              disabled={isSubmitting}
              isReadOnly={isReadOnly || isCompleted}
              isReview={!!storedIsReview}
            />
          </SHBox>
        </FormProvider>
        <SummaryDisclaimers />
      </SHStack>
    </SHContainer>
  );
};
